"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { ConfigItemImage } from "@/types/config";
import { landingConfigApi, contactGlobalApi, footerApi } from "@/api";
import {
  isValidImageUrl,
  getImageUrlWithFallback,
  getPlaceholderImage,
} from "@/utils/image";
import { ContactGlobal } from "@/types/contactType";
import { FooterGlobal } from "@/api/globals/globals.api";

interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className = "" }) => {
  const [logoConfig, setLogoConfig] = useState<ConfigItemImage | null>(null);
  const [contactData, setContactData] = useState<ContactGlobal | null>(null);
  const [footerData, setFooterData] = useState<FooterGlobal | null>(null);
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch logo config with type safety
        const logoResponse = await landingConfigApi.findOne({
          where: {
            key: { equals: "logo" },
          },
        });

        if (logoResponse.success && logoResponse.data) {
          // Cast to ConfigItemImage type
          const configData = logoResponse.data as unknown as ConfigItemImage;

          // Validate that it's an image type config with valid URL
          if (
            configData.type === "image" &&
            configData.imageValue?.url &&
            isValidImageUrl(configData.imageValue.url)
          ) {
            setLogoConfig(configData);
          } else {
            console.warn(
              "Logo config is not an image type or missing imageValue/url",
            );
          }
        } else if (!logoResponse.success) {
          console.error("Failed to fetch logo:", logoResponse.error?.message);
        }

        // Fetch contact data (optional, fallback to static content)
        try {
          const contactResponse = await contactGlobalApi.get();
          if (contactResponse.success && contactResponse.data) {
            setContactData(contactResponse.data);
          }
        } catch (contactError) {
          // Contact API is optional, continue with static content
          console.log("Contact API not available, using static content");
        }

        // Fetch footer data for copyright (optional, fallback to static content)
        // try {
        //   const footerResponse = await footerApi.get();
        //   if (footerResponse.success && footerResponse.data) {
        //     setFooterData(footerResponse.data);
        //   }
        // } catch (footerError) {
        //   // Footer API is optional, continue with static content
        //   console.log("Footer API not available, using static content");
        // }
      } catch (error) {
        console.error("Error fetching data from API:", error);
      }
    };

    fetchData();
  }, []);
  return (
    <footer className={`bg-gray-900 text-white ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info Section */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-2">
            <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-4 mb-6">
              {/* Logo */}
              <div className="flex-shrink-0 mb-4 sm:mb-0">
                <img
                  src={getImageUrlWithFallback(
                    logoConfig?.imageValue?.url,
                    getPlaceholderImage(64, 64, "KTX"),
                  )}
                  alt={
                    logoConfig?.imageValue?.alt || logoConfig?.label || "Logo"
                  }
                  title={logoConfig?.label}
                  className="h-16 w-16 rounded-lg object-contain bg-white/10 p-2"
                />
              </div>

              {/* Company Name & Tagline */}
              <div className="flex-1">
                <h3 className="text-xl lg:text-2xl font-bold mb-2 text-white">
                  Số hóa nhà vệ sinh Việt Nam
                </h3>
                <p className="text-green-400 text-sm font-medium mb-2">
                  Sạch - Xanh - Tuần Hoàn
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Đồng hành cùng mọi gia đình Việt trong hành trình xây dựng lối
                  sống xanh, bền vững.
                </p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="mt-8 space-y-3">
              <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
                Liên hệ
              </h4>
              <p className="text-gray-400 text-sm flex items-center hover:text-white transition-colors duration-200">
                <svg
                  className="w-4 h-4 mr-3 text-green-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <a
                  href={`tel:${contactData?.phone?.replace(/\s/g, "") || "1900xxxx"}`}
                  className="hover:text-green-400"
                >
                  {contactData?.phone || "1900 xxxx"}
                </a>
              </p>
              <p className="text-gray-400 text-sm flex items-center hover:text-white transition-colors duration-200">
                <svg
                  className="w-4 h-4 mr-3 text-green-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <a
                  href={`mailto:${contactData?.email || "info@ktx.vn"}`}
                  className="hover:text-green-400"
                >
                  {contactData?.email || "info@ktx.vn"}
                </a>
              </p>
              {contactData?.website && (
                <p className="text-gray-400 text-sm flex items-center hover:text-white transition-colors duration-200">
                  <svg
                    className="w-4 h-4 mr-3 text-green-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"
                    />
                  </svg>
                  <a
                    href={
                      contactData.website.startsWith("http")
                        ? contactData.website
                        : `https://${contactData.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-400"
                  >
                    {contactData.website}
                  </a>
                </p>
              )}
              <p className="text-gray-400 text-sm flex items-start hover:text-white transition-colors duration-200">
                <svg
                  className="w-4 h-4 mr-3 mt-0.5 text-green-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>
                  {contactData?.address ||
                    "Tòa nhà ABC, Quận XYZ, TP. Hồ Chí Minh"}
                </span>
              </p>
            </div>
          </div>

          {/* Social Media Section */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">
              Mạng xã hội
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  href="https://facebook.com"
                  target="_blank"
                  className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Facebook
                </Link>
              </li>
              <li>
                <Link
                  href="https://instagram.com"
                  target="_blank"
                  className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z" />
                  </svg>
                  Instagram
                </Link>
              </li>
              <li>
                <Link
                  href="https://twitter.com"
                  target="_blank"
                  className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                  Twitter
                </Link>
              </li>
              <li>
                <Link
                  href="https://youtube.com"
                  target="_blank"
                  className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                  YouTube
                </Link>
              </li>
              <li>
                <Link
                  href="https://linkedin.com"
                  target="_blank"
                  className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                </Link>
              </li>
            </ul>
          </div>

          {/* Partners Section */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Đối tác</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  href="#"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Nhà nước
                </Link>
              </li>
              <li>
                <Link
                  href="#"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Doanh nghiệp
                </Link>
              </li>
              <li>
                <Link
                  href="#"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Hiệp hội
                </Link>
              </li>
              <li>
                <Link
                  href="#"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Cơ nhân/hộ gia đình
                </Link>
              </li>
            </ul>

            {/* Quick Links */}
            <h4 className="text-lg font-semibold mb-6 mt-8 text-white">
              Liên kết nhanh
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/about"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Về chúng tôi
                </Link>
              </li>
              <li>
                <Link
                  href="/partners"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Đối tác
                </Link>
              </li>
              <li>
                <Link
                  href="/green-roadmap"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Lộ trình Xanh
                </Link>
              </li>
              <li>
                <Link
                  href="/project"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Dự án
                </Link>
              </li>
              <li>
                <Link
                  href="/"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Trang chủ
                </Link>
              </li>
              <li>
                <Link
                  href="/contact"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Liên hệ
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <div className="text-center lg:text-left">
              <p className="text-gray-400 text-sm">
                {footerData?.copyright ||
                  "© 2025 KTX System. All rights reserved."}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Được phát triển bởi đội ngũ KTX Tech
              </p>
            </div>
            <div className="flex flex-wrap justify-center lg:justify-end gap-4 lg:gap-6">
              <Link
                href="#"
                className="text-gray-400 hover:text-green-400 text-sm transition-colors duration-200"
              >
                Điều khoản sử dụng
              </Link>
              <Link
                href="#"
                className="text-gray-400 hover:text-green-400 text-sm transition-colors duration-200"
              >
                Chính sách bảo mật
              </Link>
              <Link
                href="#"
                className="text-gray-400 hover:text-green-400 text-sm transition-colors duration-200"
              >
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
